"use client";

import { CheckIcon } from "../CheckIcon";
import { FeatureList } from "../FeatureList";
import { FlowSelect } from "../FlowSelect";
import { OrderSummaryCard } from "../OrderSummaryCard";
import { PlanCard } from "../PlanCard";
import { ReviewsRow } from "../ReviewsRow";
import { SHAREHOLDER_OPTIONS } from "../options";
import { PLANS, type PackageSummary } from "../pricing";

const BENEFITS = [
  "Unlimited company changes",
  "Designated Representative at no extra cost",
  "Local experts in Hong Kong regulations",
];

// Same two lists as the register branch's secretary screen — still reading
// like placeholder content on the left (PayPal/Airwallex/Stripe).
const INCLUDED = [
  "HK Company Registration",
  "PayPal with VIP Support (6% dispute tolerance)",
  "Payoneer VIP Account and 5+ more banks",
  "Airwallex Account",
  
  "Access to 9+ PayPal Reps",
  "Unlimited PayPal Reactivations",
  "Stripe",
];

const INCLUDED_ADVANCED = [
  "Change of company name (government fee excluded)",
  "Allotment of Shares",
  "Transfer of Shares",
  "Change of director (appointment/cessation)",
];

export function ManageSecretaryStep({
  shareholders,
  onShareholdersChange,
  plan,
  onPlanChange,
  summary,
  onNext,
}: {
  shareholders: string;
  onShareholdersChange: (value: string) => void;
  plan: string | null;
  onPlanChange: (value: string) => void;
  summary: PackageSummary;
  onNext: () => void;
}) {
  // The advanced list is premium-only, so it reframes itself against the
  // current choice, as on the register branch.
  const premiumName = PLANS.find((p) => p.id === "premium")?.name ?? "Premium";
  const advancedIncluded = plan === "premium";
  const advancedLocked = plan === "standard";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (plan) onNext();
      }}
      className="mx-auto w-full max-w-[1250px] px-4 pb-16 pt-8 sm:px-6 sm:pb-24 sm:pt-12 lg:pt-[31px]"
    >
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        {/* Left column. items-end only from lg — below that it would push the
            non-stretching children right of everything else. */}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-6 sm:gap-8 lg:items-end">
          <h1 className="w-full font-suisse-intl-bold text-[26px] leading-tight text-white sm:text-4xl md:text-5xl lg:text-[56px] lg:leading-[71px]">
            Expert Company Secretary for end to end compliance
          </h1>

          <p className="w-full font-suisse-intl-medium text-base leading-relaxed text-white sm:text-lg sm:leading-[28.8px]">
            Your Annual Return, statutory updates, and company changes are handled by experts,
            keeping your Hong Kong company fully compliant and protected from penalties.
          </p>

          <ul className="flex w-full flex-col items-start gap-2">
            {BENEFITS.map((benefit) => (
              <li key={benefit} className="flex w-full items-start gap-2">
                <CheckIcon />
                <span className="font-suisse-intl text-base leading-7 text-white sm:text-lg sm:leading-[28.8px]">
                  {benefit}
                </span>
              </li>
            ))}
          </ul>

          <div className="flex w-full lg:justify-end">
            <ReviewsRow />
          </div>

          <div className="flex w-full flex-col items-start gap-4">
            <label
              htmlFor="manage-shareholders"
              className="font-suisse-intl-medium text-base leading-snug text-white sm:text-lg sm:leading-4"
            >
              How many shareholders do you have?
            </label>
            <FlowSelect
              id="manage-shareholders"
              value={shareholders}
              onChange={onShareholdersChange}
              options={SHAREHOLDER_OPTIONS.map((option) => ({ value: option, label: option }))}
            />
          </div>

          <div className="flex w-full flex-col items-center gap-6 sm:gap-8">
            <h2 className="w-full font-suisse-works text-[28px] leading-tight text-white [text-shadow:0px_4px_24px_rgba(0,0,0,0.05)] sm:text-[40px] sm:leading-[64px] lg:text-5xl">
              Choose a plan
            </h2>

            {/* Side by side only from lg: two cards at 640px leave too little
                room for the struck-through-plus-current price pair. */}
            <fieldset className="flex w-full flex-col gap-6 sm:gap-8 lg:flex-row lg:items-stretch">
              <legend className="sr-only">Choose a Company Secretary plan</legend>
              {PLANS.map((p) => (
                <PlanCard key={p.id} plan={p} selected={plan === p.id} onSelect={onPlanChange} />
              ))}
            </fieldset>

            <div className="flex w-full flex-col gap-6 sm:flex-row">
              <FeatureList title="What's included" items={INCLUDED} />
              <FeatureList
                title={
                  advancedLocked ? `Upgrade to ${premiumName} for these` : "Included (Advanced)"
                }
                items={INCLUDED_ADVANCED}
                tone={advancedIncluded ? "included" : advancedLocked ? "locked" : "default"}
              />
            </div>
          </div>

          {/* Full width on phones; the design's 388px right-aligned pill from
              lg, where items-end applies. */}
          <button
            type="submit"
            disabled={!plan}
            className="flex h-14 w-full items-center justify-center rounded-full bg-white px-4 font-suisse-intl text-xl leading-5 text-[#2A4775] transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50 lg:max-w-[388px]"
          >
            Next
          </button>
        </div>

        {/* Right column — the manage branch's summary: the plan only, at its
            net price. No incorporation fees, no discount row. */}
        <OrderSummaryCard summary={summary} className="lg:w-[402px] lg:shrink-0" />
      </div>
    </form>
  );
}
